"use client";

import React, { useState, useRef, useCallback, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Variable, Minus, ChevronDown } from "lucide-react";
import type { FormikProps } from "formik";
import type { SilaeFormData } from "@/types/silae";
import { useSilaeVariables } from "@/hooks/useSilaeVariables";
import { useTranslation } from "react-i18next";
import {
  generateMemoCode,
  getAllGeneratedVariablesFromBonus,
} from "@/utils/silae-memo-helpers";

interface SilaeMemoEditorProps {
  formik: FormikProps<SilaeFormData>;
  fieldPrefix?: string; // Nouveau système: chemin complet (ex: "ifBranch.bonuses.0")
}

// Types pour les éléments du mémo
interface MemoElement {
  id: string;
  type: "text" | "variable" | "separator";
  content: string;
  variableCode?: string;
  variableType?: string;
}

/**
 * Éditeur de mémos SILAE avec interface user-friendly
 * Variables colorées, séparateurs visuels, pas de code visible
 */
export const SilaeMemoEditor: React.FC<SilaeMemoEditorProps> = ({
  formik,
  fieldPrefix,
}) => {
  const { t } = useTranslation();

  // Hook pour les variables Silae depuis Redux
  const { variables: silaeVariables, findVariable } = useSilaeVariables();

  // Helper pour accéder aux valeurs imbriquées
  const getFieldValue = useCallback(
    (path: string) => {
      const keys = path.split(".");
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let value: any = formik.values;
      for (const key of keys) {
        value = value?.[key];
        if (value === undefined) return undefined;
      }
      return value;
    },
    [formik.values],
  );

  const [isVariablePopoverOpen, setIsVariablePopoverOpen] = useState(false);
  const [selectedVariable, setSelectedVariable] = useState<string>("");
  const [isGeneratedPopoverOpen, setIsGeneratedPopoverOpen] = useState(false);
  const [generatedSearch, setGeneratedSearch] = useState<string>("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Parser pour convertir le contenu en éléments structurés (une ligne = un élément)
  const parseContent = useCallback((content: string): MemoElement[] => {
    if (!content.trim()) return [];

    const elements: MemoElement[] = [];
    const lines = content.split("\n");

    lines.forEach((line, lineIndex) => {
      if (!line.trim()) return;

      // Détecter les séparateurs (lignes de tirets Unicode ou ASCII)
      if (line.trim().match(/^[-─]{10,}$/)) {
        elements.push({
          id: `sep-${lineIndex}`,
          type: "separator",
          content: line.trim(),
        });
        return;
      }

      // Détecter si la ligne contient des variables
      const variableRegex = /\{\{([A-Za-z_][A-Za-z0-9_.]*)\}\}/g;
      const hasVariables = variableRegex.test(line);

      if (hasVariables) {
        // Ligne avec variables - traiter comme une seule unité
        elements.push({
          id: `mixed-${lineIndex}`,
          type: "text", // On garde "text" mais on traitera les variables dans le rendu
          content: line.trim(),
        });
      } else {
        // Ligne de texte simple
        elements.push({
          id: `text-${lineIndex}`,
          type: "text",
          content: line.trim(),
        });
      }
    });

    return elements;
  }, []);

  // Extraire le bonus courant pour accéder à ses bases et tauxs (nouvelle structure)
  const currentBonus = useMemo(() => {
    if (!fieldPrefix) return null;
    return getFieldValue(fieldPrefix);
  }, [fieldPrefix, getFieldValue]);

  // Extraire toutes les variables générées du bonus courant (utilise la fonction exportée)
  const getAllGeneratedVariablesFromBonusLocal = useCallback(() => {
    if (!currentBonus) return new Set<string>();
    return getAllGeneratedVariablesFromBonus(currentBonus);
  }, [currentBonus]);

  // Convertir en code SILAE (une ligne = un Call Rem)
  // Utilise la fonction exportée generateMemoCode pour éviter la duplication de code
  const elementsToSilaeCode = useCallback(
    (elements: MemoElement[]): string => {
      if (elements.length === 0) return "";

      // Reconstruire le contenu depuis les éléments (une ligne par élément)
      const memoLines = elements
        .map((element) => {
          // Pour les séparateurs, on garde le contenu original
          // Pour le texte, on garde le contenu avec les variables {{variable}}
          return element.content;
        })
        .join("\n");

      // Extraire les variables générées du bonus
      const generatedVars = getAllGeneratedVariablesFromBonusLocal();

      // Utiliser la fonction exportée pour générer le code
      const memoCodeLines = generateMemoCode(
        memoLines,
        generatedVars,
        "  ", // Indentation pour le preview
      );

      return memoCodeLines.join("\n");
    },
    [getAllGeneratedVariablesFromBonusLocal],
  );

  // Valeur actuelle du mémo
  const memoFieldName = "memoLines";
  const memoContent =
    (formik.values as unknown as Record<string, string>)[memoFieldName] || "";

  // Chemin complet du champ memoLines dans Formik
  const memoFieldPath = fieldPrefix ? `${fieldPrefix}.memoLines` : "memoLines";

  // Récupérer les erreurs de validation pour ce champ
  const getFieldError = useCallback(
    (path: string): string | undefined => {
      const keys = path.split(".");
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let error: any = formik.errors;
      for (const key of keys) {
        if (error && typeof error === "object" && key in error) {
          error = error[key];
        } else {
          return undefined;
        }
      }
      return typeof error === "string" ? error : undefined;
    },
    [formik.errors],
  );

  // Récupérer si le champ a été touché
  const getFieldTouched = useCallback(
    (path: string): boolean => {
      const keys = path.split(".");
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let touched: any = formik.touched;
      for (const key of keys) {
        if (touched && typeof touched === "object" && key in touched) {
          touched = touched[key];
        } else {
          return false;
        }
      }
      return touched === true;
    },
    [formik.touched],
  );

  const memoError = getFieldError(memoFieldPath);
  const memoTouched = getFieldTouched(memoFieldPath);

  // Parser le contenu actuel
  const memoElements = useMemo(
    () => parseContent(memoContent),
    [memoContent, parseContent],
  );

  // Code SILAE généré
  const silaeCode = useMemo(
    () => elementsToSilaeCode(memoElements),
    [memoElements, elementsToSilaeCode],
  );

  // Insérer une variable à la position du curseur
  const insertVariable = useCallback(
    (variableCode: string) => {
      const variable = findVariable(variableCode);
      const generatedVars = getAllGeneratedVariablesFromBonusLocal();
      const isGenerated =
        variableCode === "Bases" ||
        variableCode === "Tauxs" ||
        generatedVars.has(variableCode);

      if (!variable && !isGenerated) return;

      const textarea = textareaRef.current;
      if (!textarea) return;

      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const currentValue = textarea.value;
      const insertText = `{{${variableCode}}}`;

      const newValue =
        currentValue.substring(0, start) +
        insertText +
        currentValue.substring(end);

      formik.setFieldValue(memoFieldPath, newValue);

      // Repositionner le curseur après l'insertion
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(
          start + insertText.length,
          start + insertText.length,
        );
      }, 0);

      setIsVariablePopoverOpen(false);
      setSelectedVariable("");
    },
    [formik, memoFieldPath, getAllGeneratedVariablesFromBonusLocal],
  );

  // Insérer un séparateur à la ligne suivante après le curseur
  const insertSeparator = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const currentValue = textarea.value;
    const separatorLine = "─".repeat(50);

    // Trouver la fin de la ligne actuelle
    let lineEnd = start;
    while (lineEnd < currentValue.length && currentValue[lineEnd] !== "\n") {
      lineEnd++;
    }

    // Insérer le séparateur à la ligne suivante
    const insertText = "\n" + separatorLine + "\n";

    const newValue =
      currentValue.substring(0, lineEnd) +
      insertText +
      currentValue.substring(end);

    formik.setFieldValue(memoFieldPath, newValue);

    // Repositionner le curseur à la ligne suivante après le séparateur
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(
        lineEnd + insertText.length,
        lineEnd + insertText.length,
      );
    }, 0);
  }, [formik, memoFieldPath]);

  // Variables filtrées pour la recherche (exclut les fonctions)
  const filteredVariables = useMemo(() => {
    if (!selectedVariable) {
      return silaeVariables
        .filter((variable) => variable.type !== "function")
        .slice(0, 20);
    }

    return silaeVariables
      .filter(
        (variable) =>
          variable.type !== "function" &&
          (variable.code
            .toLowerCase()
            .includes(selectedVariable.toLowerCase()) ||
            (variable.definition &&
              variable.definition
                .toLowerCase()
                .includes(selectedVariable.toLowerCase()))),
      )
      .slice(0, 20);
  }, [selectedVariable, silaeVariables]);

  // Fonction récursive pour extraire les variables intermédiaires avec leurs définitions
  const extractVariablesWithDefinitions = useCallback(
    (
      complexExpression: Array<{
        variableName?: string;
        sourceType?: string;
        complexExpression?: Array<unknown>;
      }>,
      vars: Array<{ code: string; definition: string }>,
      prefix: string,
      level: number = 0,
    ) => {
      if (!complexExpression || !Array.isArray(complexExpression)) {
        return;
      }

      let index = 1;
      complexExpression.forEach((item) => {
        // Ajouter la variable intermédiaire si elle existe
        if (
          item.variableName &&
          !vars.some((v) => v.code === item.variableName)
        ) {
          const levelPrefix = level > 0 ? ` (niveau ${level + 1})` : "";
          vars.push({
            code: item.variableName,
            definition: `${prefix} ${index}${levelPrefix}`,
          });
          index++;
        }

        // Si l'item est lui-même une expression complexe, parcourir récursivement
        if (
          item.sourceType === "complex_expression" &&
          item.complexExpression &&
          Array.isArray(item.complexExpression)
        ) {
          extractVariablesWithDefinitions(
            item.complexExpression as Array<{
              variableName?: string;
              sourceType?: string;
              complexExpression?: Array<unknown>;
            }>,
            vars,
            prefix,
            level + 1,
          );
        }
      });
    },
    [],
  );

  // Variables générées du bonus courant (nouvelle structure hiérarchique)
  const currentGeneratedVariables = useMemo(() => {
    const vars: Array<{ code: string; definition: string }> = [];

    // Toujours ajouter Bases
    vars.push({
      code: "Bases",
      definition: "Montant de base calculé",
    });

    // Ajouter Tauxs si la méthode de calcul utilise des taux
    if (
      currentBonus?.calculMethod === "1" ||
      currentBonus?.calculMethod === "3"
    ) {
      vars.push({
        code: "Tauxs",
        definition: "Taux calculé",
      });
    }

    // Parcourir les bases pour les variables intermédiaires (récursif)
    if (currentBonus?.bases && Array.isArray(currentBonus.bases)) {
      currentBonus.bases.forEach(
        (base: {
          complexExpression?: Array<{
            variableName?: string;
            sourceType?: string;
            complexExpression?: Array<unknown>;
          }>;
        }) => {
          if (base.complexExpression && Array.isArray(base.complexExpression)) {
            extractVariablesWithDefinitions(
              base.complexExpression,
              vars,
              "Var intermédiaire base",
            );
          }
        },
      );
    }

    // Parcourir les tauxs pour les variables intermédiaires (récursif)
    if (currentBonus?.tauxs && Array.isArray(currentBonus.tauxs)) {
      currentBonus.tauxs.forEach(
        (taux: {
          complexExpression?: Array<{
            variableName?: string;
            sourceType?: string;
            complexExpression?: Array<unknown>;
          }>;
        }) => {
          if (taux.complexExpression && Array.isArray(taux.complexExpression)) {
            extractVariablesWithDefinitions(
              taux.complexExpression,
              vars,
              "Var intermédiaire taux",
            );
          }
        },
      );
    }

    return vars;
  }, [currentBonus, extractVariablesWithDefinitions]);

  const filteredGeneratedVariables = useMemo(() => {
    if (!generatedSearch.trim()) return currentGeneratedVariables;
    const q = generatedSearch.toLowerCase();
    return currentGeneratedVariables.filter(
      (v) =>
        v.code.toLowerCase().includes(q) ||
        (v.definition && v.definition.toLowerCase().includes(q)),
    );
  }, [currentGeneratedVariables, generatedSearch]);

  // Rendu des éléments avec coloration
  const renderElements = useCallback(() => {
    return memoElements.map((element, index) => {
      switch (element.type) {
        case "separator":
          return (
            <div
              key={element.id}
              className="flex items-center gap-2 py-2 text-gray-400"
            >
              <div className="flex-1 border-t border-gray-300"></div>
              <span className="text-xs font-medium">
                {t("silaeMemoEditor.separatorLabel")}
              </span>
              <div className="flex-1 border-t border-gray-300"></div>
            </div>
          );
        case "text": {
          // Traiter les variables dans le texte pour l'affichage
          const variableRegex = /\{\{([A-Za-z_][A-Za-z0-9_.]*)\}\}/g;
          const content = element.content;

          if (variableRegex.test(content)) {
            // Ligne avec variables - parser et colorier
            const parts = [];
            let lastIndex = 0;
            let match;

            // Reset regex
            variableRegex.lastIndex = 0;

            while ((match = variableRegex.exec(content)) !== null) {
              // Ajouter le texte avant la variable
              if (match.index > lastIndex) {
                const textBefore = content.substring(lastIndex, match.index);
                parts.push(
                  <span
                    key={`text-${index}-${lastIndex}`}
                    className="text-foreground"
                  >
                    {textBefore}
                  </span>,
                );
              }

              // Ajouter la variable colorée
              const variableCode = match[1];
              const variable = findVariable(variableCode);
              parts.push(
                <span
                  key={`var-${index}-${match.index}`}
                  className="inline-flex items-center gap-1 text-yellow-600 font-medium px-2 py-1 rounded-md bg-yellow-100 dark:bg-yellow-900/20 border border-yellow-500 dark:border-yellow-600 text-sm "
                  title={`Variable SILAE: ${variableCode} (${variable?.type || "Unknown"})`}
                >
                  {variableCode}
                </span>,
              );

              lastIndex = match.index + match[0].length;
            }

            // Ajouter le texte restant
            if (lastIndex < content.length) {
              const textAfter = content.substring(lastIndex);
              parts.push(
                <span
                  key={`text-${index}-${lastIndex}`}
                  className="text-foreground"
                >
                  {textAfter}
                </span>,
              );
            }

            return (
              <div
                key={element.id}
                className="flex flex-wrap items-center gap-1"
              >
                {parts}
              </div>
            );
          } else {
            // Texte simple sans variables
            return (
              <span key={element.id} className="text-foreground">
                {content}
              </span>
            );
          }
        }
        default:
          return null;
      }
    });
  }, [memoElements, t]);

  return (
    <div className="space-y-6">
      {/* Zone d'édition principale */}
      <Card>
        <CardHeader>
          <div className="flex justify-between w-full items-center gap-2">
            <CardTitle className="text-lg">
              {t("silaeMemoEditor.title")}
            </CardTitle>
            <div className="flex items-center gap-2">
              {/* Bouton variable SILAE */}
              <Popover
                open={isVariablePopoverOpen}
                onOpenChange={setIsVariablePopoverOpen}
              >
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="gap-2"
                  >
                    <Variable className="h-4 w-4" />
                    {t("silaeMemoEditor.variableButton")}
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-80 p-0" align="start">
                  <Command>
                    <CommandInput
                      placeholder={t("silaeMemoEditor.searchPlaceholder")}
                      value={selectedVariable}
                      onValueChange={setSelectedVariable}
                    />
                    <CommandList>
                      <CommandEmpty>
                        {t("silaeMemoEditor.noVariablesFound")}
                      </CommandEmpty>
                      <CommandGroup>
                        {filteredVariables.map((variable) => (
                          <CommandItem
                            key={variable.code}
                            value={variable.code}
                            onSelect={() => insertVariable(variable.code)}
                            className="flex flex-col items-start gap-1 p-3"
                          >
                            <div className="flex items-center gap-2 w-full">
                              <code className="text-sm font-mono bg-muted px-1 rounded">
                                {variable.code}
                              </code>
                              <Badge variant="outline" className="text-xs">
                                {variable.type}
                              </Badge>
                            </div>
                            <p className="text-xs text-muted-foreground">
                              {variable.definition}
                            </p>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>

              {/* Bouton variables générées (selon contexte: principal ou else) */}
              <Popover
                open={isGeneratedPopoverOpen}
                onOpenChange={setIsGeneratedPopoverOpen}
              >
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="gap-2"
                  >
                    <Variable className="h-4 w-4" />
                    Variables générée
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-80 p-0" align="start">
                  <Command>
                    <CommandInput
                      placeholder={t("silaeMemoEditor.searchPlaceholder")}
                      value={generatedSearch}
                      onValueChange={setGeneratedSearch}
                    />
                    <CommandList>
                      <CommandEmpty>
                        {t("silaeMemoEditor.noVariablesFound")}
                      </CommandEmpty>
                      <CommandGroup>
                        {filteredGeneratedVariables.map((variable) => (
                          <CommandItem
                            key={variable.code}
                            value={variable.code}
                            onSelect={() => {
                              insertVariable(variable.code);
                              setIsGeneratedPopoverOpen(false);
                              setGeneratedSearch("");
                            }}
                            className="flex flex-col items-start gap-1 p-3"
                          >
                            <div className="flex items-center gap-2 w-full">
                              <code className="text-sm font-mono bg-muted px-1 rounded">
                                {variable.code}
                              </code>
                            </div>
                            <p className="text-xs text-muted-foreground">
                              {variable.definition}
                            </p>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>

              {/* Bouton séparateur */}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={insertSeparator}
                className="gap-2"
              >
                <Minus className="h-4 w-4" />
                {t("silaeMemoEditor.separatorButton")}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Éditeur principal */}
          <div className="space-y-2">
            <textarea
              ref={textareaRef}
              placeholder={t("silaeMemoEditor.textareaPlaceholder")}
              value={memoContent}
              onChange={(e) => {
                formik.setFieldValue(memoFieldPath, e.target.value);
              }}
              onBlur={() => {
                formik.setFieldTouched(memoFieldPath, true);
              }}
              className={`w-full min-h-[300px] font-mono text-sm border rounded-lg p-4 resize-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 border-neutral-200 dark:border-neutral-700 bg-background ${
                memoError && memoTouched
                  ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                  : ""
              }`}
            />
            {/* Affichage de l'erreur */}
            {memoError && memoTouched && (
              <p className="text-sm text-red-600 dark:text-red-400">
                {memoError}
              </p>
            )}
          </div>

          {/* Aperçu visuel des éléments parsés */}
          {memoElements.length > 0 && (
            <div className="border rounded-lg p-4 bg-muted">
              <h4 className="text-sm font-medium mb-3 text-muted-foreground">
                {t("silaeMemoEditor.previewTitle")}
              </h4>
              <div className="space-y-2">{renderElements()}</div>
            </div>
          )}

          <div className="text-xs text-muted-foreground space-y-1">
            <p>
              <strong>{t("silaeMemoEditor.notes.title")}</strong>
            </p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>
                {t("silaeMemoEditor.notes.variables")}{" "}
                <span className="text-yellow-600 font-medium px-2 py-1 rounded-md bg-yellow-100 dark:bg-yellow-900/20 border border-yellow-500 dark:border-yellow-600">
                  {t("silaeMemoEditor.notes.yellow")}
                </span>
              </li>
              <li>{t("silaeMemoEditor.notes.format")}</li>
            </ul>
          </div>
        </CardContent>
      </Card>

    </div>
  );
};
